import { crmMemberFace } from "../org/crmMemberFace";
import { normalizeCrmOwnerId } from "../crmRowAccess";
import type { OrgDirectoryMember } from "../../../../@types/webotServer";

/**
 * A HANDLER, RESOLVED — a plain value, deliberately not a row and not a server payload.
 *
 * The server sends only a `userId`; the name and photo come from `useOrgDirectory`, which is
 * live, module-cached, and already in memory on every screen this renders on. Same choice
 * and the same reasoning as `crmMemberFace` — a row's stamped `ownerName`/`ownerAvatarUrl`
 * are a write-time snapshot off the contact document, so they are absent on anything written
 * before that Cloud Function existed and stale for anyone who has changed their photo since.
 *
 * ⚠️ HERE THE DIRECTORY IS NOT MERELY BETTER, IT IS THE ONLY SOURCE. A designated handler
 * may hold no copy of this contact at all — that is the case the assign flow exists to fix —
 * so there is no stamped `ownersData` anywhere to fall back to.
 *
 * `name` is therefore never empty: an assignee absent from the directory (they left the
 * agency) resolves to their email, which `stringAvatar` still turns into recognisable tinted
 * initials. "שויך לטיפול: " with nothing after it is worse than an address.
 */
export type CrmHandlerView = {
  userId: string;
  name: string;
  avatarUrl: string | null;
  /** So no consumer re-derives "is this me" and gets the email casing wrong. */
  isMe: boolean;
  /** The agency this designation belongs to — a caller can be in more than one. */
  orgId: string;
  /** The assignee is no longer in that agency's roster. Renders as "מטפל קודם". */
  stale: boolean;
};

export const crmHandlerView = (
  assignment: { assigneeUserId: string; orgId: string; stale: boolean } | null | undefined,
  member: OrgDirectoryMember | null,
  viewerUserId: string | null | undefined
): CrmHandlerView | null => {
  const userId = normalizeCrmOwnerId(assignment?.assigneeUserId);
  if (!assignment || !userId) return null;
  const face = crmMemberFace(member);
  return {
    userId,
    name: face.name ?? userId,
    avatarUrl: face.avatarUrl,
    // Reuses `crmRowAccess.normalizeCrmOwnerId`, so the badge and the write path can never
    // disagree about whether two spellings of an address name the same agent.
    isMe: userId === normalizeCrmOwnerId(viewerUserId),
    orgId: assignment.orgId,
    stale: assignment.stale
  };
};
